/**
 * RadioButton component
 *
 * Renders a styled radio button with optional label, disabled state,
 * background styling, and support for controlled selection.
 *
 * @param {object} props
 * @param {string} props.id
 *   The id attribute for the radio input.
 *
 * @param {string} props.name
 *   The name attribute for the radio input. Used to group radio buttons together.
 *
 * @param {string} props.value
 *   The value attribute for the radio input.
 *
 * @param {string} props.label
 *   Optional label displayed next to the radio button.
 *
 * @param {boolean} props.enabled
 *   Determines whether the radio button is enabled or disabled. Defaults to `true`.
 *
 * @param {boolean} props.showBackground
 *   If true, adds a background and rounded styling to the entire radio button container. Defaults to `false`.
 *
 * @param {boolean} props.checked
 *   Controls whether the radio button is selected. Defaults to `false`.
 *
 * @param {function} props.onChange
 *   Callback function invoked when the radio button value changes.
 *
 * @param {boolean} [props.showLabel=true]
 *   If false, hides the label text next to the radio button.
 */
export default function RadioButton({
    id = "id",
    name = "name",
    value = "value",
    label = "label",
    enabled = true,
    showBackground = false,
    checked = false,
    showLabel = true,
    onChange,
    ...props
}) {
    const labelClass = enabled
        ? "text-main-typography-system-default"
        : "text-main-typography-system-caption";

    const styleClass = showBackground
        ? "bg-creation-neutral-container-surface-default rounded-md"
        : "";

    return (
        <div className={`inline-flex p-2 ${styleClass}`}>
            <label className="flex items-center cursor-pointer relative">
                <input
                    type="radio"
                    id={id}
                    name={name}
                    value={value}
                    disabled={!enabled}
                    checked={checked}
                    onChange={onChange}
                    className="appearance-none peer absolute opacity-0 w-0 h-0 toggleable-radio"
                    {...props}
                />
                <div className="
          relative w-scale-md h-scale-md border rounded-round flex-shrink-0
          bg-creation-neutral-container-surface-default border-main-border-system-default text-transparent
          peer-disabled:bg-creation-neutral-container-surface-disabled
          peer-disabled:border-creation-neutral-solid-on-surface-disabled
          peer-disabled:text-transparent
          peer-checked:bg-creation-primary-solid-surface-focusing
          peer-checked:border-transparent
          peer-checked:text-creation-primary-container-surface-default
          peer-hover:bg-creation-primary-container-surface-hovered
          peer-hover:border-transparent
          peer-hover:text-creation-primary-solid-surface-hovered
          peer-hover:peer-checked:bg-creation-primary-solid-surface-hovered
          peer-hover:peer-checked:border-transparent
          peer-hover:peer-checked:text-creation-primary-container-surface-hovered
          peer-disabled:peer-checked:bg-creation-primary-container-surface-disabled
          peer-disabled:peer-checked:border-transparent
          peer-disabled:peer-checked:text-creation-primary-solid-surface-disabled
        ">
                    <div className="absolute inset-0 m-auto w-scale-sm h-scale-sm rounded-round bg-current"></div>
                </div>
            </label>
            {showLabel && <span className={`flex items-center px-2 font-ui font-body text-body-base ${labelClass}`}>
                {label}
            </span>}


        </div>
    );
}
